import React from 'react';
import type { SessionLite } from '../../../../../types/session-lite';
import apiCoreAdapter from '../../../../../hooks/apiCoreAdapter';
import { toastUtils } from '../../../../../hooks/useToast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SESSION_APPOINTMENT_TYPES } from '../../../../../hooks/api/config';
import { useAuth } from '../../../../../contexts/AuthContext';
import { parseSessionDate } from '../../../../../utils/sessions';
import { checkSessionAvailability, isMockSession } from '../../../../../utils/sessionAvailability';

import { activityTracker } from "../../../../../utils/activityTracker";
type Props = {
  session: SessionLite;
  isNextJoinable?: boolean;
};

export const JoinSessionButton: React.FC<Props> = ({ session, isNextJoinable }) => {
  const { user } = useAuth();
  const nameExtras = (session as unknown as { lastName?: string; surname?: string });
  const lastName = nameExtras.lastName ?? nameExtras.surname ?? '';
  const fullName = lastName ? `${session.patientName} ${lastName}` : session.patientName;

  const [openingMeet, setOpeningMeet] = React.useState(false);
  const [showJoinConfirm, setShowJoinConfirm] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);

  const sessionMs = React.useMemo(() => parseSessionDate(session.date).getTime(), [session.date]);

  const sessionEndMs = React.useMemo(() => {
    const matchedType = SESSION_APPOINTMENT_TYPES.find(t => t.name === session.therapyType);
    const duration = matchedType?.duration || 60;
    return sessionMs + duration * 60000;
  }, [sessionMs, session.therapyType]);

  const isJoinable = React.useMemo(() => {
    const now = Date.now();
    const diff = sessionMs - now;
    // Show button from 6h before start until session end
    return diff <= 6 * 3600000 && now < sessionEndMs;
  }, [sessionMs, sessionEndMs]);

  const getTimeRemainingLabel = React.useCallback((): string => {
    const diff = sessionMs - Date.now();
    if (diff <= 0) return 'unos instantes';
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    if (h > 0 && m > 0) return `${h} hora${h !== 1 ? 's' : ''} y ${m} minuto${m !== 1 ? 's' : ''}`;
    if (h > 0) return `${h} hora${h !== 1 ? 's' : ''}`;
    return `${m} minuto${m !== 1 ? 's' : ''}`;
  }, [sessionMs]);

  const handleOpenGoogleMeet = React.useCallback(async () => {
    if (openingMeet) return;
    try {
      setOpeningMeet(true);
      const resp = await apiCoreAdapter.getExternalToolUrl('google meet');
      const url = (resp as unknown as { content?: string })?.content;
      if (url && typeof url === 'string') {
        const meetUrl = user?.email ? `${url}?authuser=${user.email}` : url;
        window.open(meetUrl, '_blank', 'noopener,noreferrer');
        activityTracker.trackAction('meet_opened');
      } else {
        try { toastUtils.error('No se encontró el enlace de Google Meet'); } catch { /* noop */ }
      }
    } catch {
      try { toastUtils.error('No se pudo obtener el enlace de Google Meet'); } catch { /* noop */ }
    } finally {
      setOpeningMeet(false);
    }
  }, [openingMeet]);

  const handleJoinClick = React.useCallback(async () => {
    if (verifying) return;
    // Sesiones mock no se verifican (no existen aún en la DB).
    if (isMockSession(session.id, session.status)) {
      setShowJoinConfirm(true);
      return;
    }
    try {
      setVerifying(true);
      const availability = await checkSessionAvailability(session.id, session.date);
      if (availability === 'unavailable') {
        try {
          toastUtils.error('Esta sesión ya no está disponible. Es posible que el paciente la haya cancelado o reprogramado desde otra plataforma.');
        } catch { /* noop */ }
        return;
      }
    } catch {
      // Fail-open ante errores: no bloquear el ingreso.
    } finally {
      setVerifying(false);
    }
    setShowJoinConfirm(true);
  }, [verifying, session.id, session.status, session.date]);

  if (!isJoinable || !isNextJoinable || session.isCancelled) return null;

  return (
    <>
      <Button
        variant="primary"
        style={{ borderRadius: '9999px', height: '35px', fontSize: '0.8125rem' }}
        onClick={handleJoinClick}
        disabled={openingMeet || verifying}
      >
        {verifying ? 'Verificando…' : openingMeet ? 'Cargando...' : 'Ingresar a la sesión'}
      </Button>

      <Dialog open={showJoinConfirm} onOpenChange={setShowJoinConfirm}>
        <DialogContent style={{ maxWidth: '400px', padding: '1.5rem' }}>
          <DialogHeader>
            <DialogTitle>Ingresar a la sesión</DialogTitle>
          </DialogHeader>
          <div className="mt-1 mb-4">
            {sessionMs > Date.now() && (
              <>
                <p className="text-sm text-muted-foreground">
                  Tu sesión con <strong>{fullName}</strong> comienza en:
                </p>
                <p className="text-base font-semibold text-foreground text-center my-2">
                  {getTimeRemainingLabel()}
                </p>
              </>
            )}
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Recordatorio:</strong> asegurate de estar logueada en Google Meet con tu cuenta de <strong>@psimammoliti</strong> para evitar errores al ingresar.
              </p>
            </div>
            <p className="text-sm text-muted-foreground mt-3">¿Querés ingresar al meet ahora?</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowJoinConfirm(false)}>
              Volver
            </Button>
            <Button onClick={() => { setShowJoinConfirm(false); handleOpenGoogleMeet(); }}>
              Sí, ingresar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default JoinSessionButton;
